import {ShieldLockIcon} from '@primer/octicons-react'
import {Banner, type BannerProps} from '@primer/react'

import {MAX_DISPLAYED_ORGS} from '../constants'
import type {SsoOrganization} from '../types'
import styles from './CoreSsoBanner.module.css'

export interface CoreSsoBannerProps extends Pick<BannerProps, 'primaryAction' | 'onDismiss'> {
  ssoOrgs: SsoOrganization[]
}

function Description({ssoOrgs}: CoreSsoBannerProps) {
  if (ssoOrgs.length === 0) return <>Single sign-on to see results</>

  const displayOrgs = ssoOrgs.slice(0, MAX_DISPLAYED_ORGS)
  const remainingCount = ssoOrgs.length - MAX_DISPLAYED_ORGS
  const organizationText = ssoOrgs.length === 1 ? 'organization' : 'organizations'

  const formatOrgList = () => {
    const formattedOrgNames = displayOrgs.map(org => <strong key={org.login}>{org.name}</strong>)

    if (formattedOrgNames.length === 1) {
      return formattedOrgNames[0]
    } else if (formattedOrgNames.length === 2) {
      return (
        <>
          {formattedOrgNames[0]} and {formattedOrgNames[1]}
        </>
      )
    } else {
      // MAX_DISPLAYED_ORGS orgs, with potential overflow
      const lastIndex = formattedOrgNames.length - 1
      const baseList = (
        <>
          {formattedOrgNames[0]}, {formattedOrgNames[1]}, and {formattedOrgNames[lastIndex]}
        </>
      )
      return remainingCount > 0 ? (
        <>
          {baseList}, and {remainingCount} more
        </>
      ) : (
        baseList
      )
    }
  }

  return (
    <>
      Single sign-on to see results in the {formatOrgList()} {organizationText}
    </>
  )
}

/**
 * Bare minimum UI to display a non-interactive SSO banner, shared between suspense fallback and main component.
 * The goal here is to look almost identical to the fully rendered banner while requiring as few bytes as possible to
 * show. So this component eliminates all dependencies and interactivity, restricting code to just the top-level
 * visible UI.
 */
export function CoreSsoBanner({ssoOrgs, onDismiss, primaryAction}: CoreSsoBannerProps) {
  return (
    <Banner
      title="Single sign-on"
      className={styles.banner}
      hideTitle
      icon={<ShieldLockIcon />}
      description={<Description ssoOrgs={ssoOrgs} />}
      onDismiss={onDismiss}
      primaryAction={primaryAction}
      data-testid="global-sso-banner"
    />
  )
}
